/* eslint-disable no-console */
import { Component, Ref, Prop } from '@eastsideco/salvo-ts';
import { ThemeComponent } from '@/theme/ThemeComponent';
import { cart } from '@/theme/Cart';

interface ZapietEvents {
    // https://support.zapiet.com/en/articles/6279764-widget-api
    ['widget_loaded'](): void;
    ['checkoutEnabled'](): void;
    ['selected_method'](payload: 'delivery'|'pickup'|'shipping'): void;
}

// Setup interface and function for checking if Zapiet is present
declare global {
    interface Window {
        initializeZapietApp?(): unknown;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        ZapietEvent?: {
            listen<T extends keyof ZapietEvents>(event: T, cb: ZapietEvents[T]): void;
        };
    }
}
const ZAPIET_CODE_MARKER = 'Zapiet.start(';
export function isZapietPresent(): boolean {
    return !!window.initializeZapietApp && window.initializeZapietApp.toString().indexOf(ZAPIET_CODE_MARKER) > -1;
}

@Component
export class ZapietContainer extends ThemeComponent {
    @Ref zapietWidget!: HTMLElement;
    @Ref customMethodButtonsCotainer!: HTMLElement;
    @Ref customMethodButton!: HTMLButtonElement[];

    @Prop active!: boolean; // active if using custom buttons

    init() {

        // Ensure Zapiet is present (it can be disabled via Theme Settings => App Embeds)
        if (!isZapietPresent() && this.active) {
            // Hide our custom buttons and do not continue
            this.customMethodButtonsCotainer.classList.add('zapiet-container__method-buttons--hidden');
            return;
        }

        if (this.active) {
        // Listen for custom method button clicks
            for (const methodButton of this.customMethodButton) {
                this.$listen(methodButton, 'click', (event) => {
                    this.handleMethodButtonClick(methodButton);
                });
            }
        }

        // Subscribe to cart change events
        document.addEventListener('cartChanged', () => {
            if (cart.isEmpty()) {
                this.classList.add('zapiet-container--hidden');
            } else {
                this.classList.remove('zapiet-container--hidden');
            }
            if (window.initializeZapietApp){
                window.initializeZapietApp();
            }
        });

        // Function to wait for ZapietObject to become available
        function waitForZapietObject() {
            return new Promise((resolve, reject) => {
                const intervalId = setInterval(() => {
                    if (window.ZapietEvent) {
                        clearInterval(intervalId);
                        resolve(window.ZapietEvent);
                    }
                }, 100); // Check every 100 milliseconds for availability of thatObject
            });
        }

        if (typeof window.ZapietEvent === 'undefined') {
            void waitForZapietObject().then((thatObject) => {
                this.zapietEventListeners();
            });
        } else {
            this.zapietEventListeners();
        }
    }

    zapietEventListeners() {

        const checkoutButton = document.querySelector('.cart__checkout-button') as HTMLElement | null;

        window.ZapietEvent?.listen('selected_method', (p) => {
            if(checkoutButton) {
                checkoutButton.classList.add('button--disabled');
                checkoutButton.innerText = 'Please Wait';
            }
        });

        window.ZapietEvent?.listen('checkoutEnabled', () => {
            if(checkoutButton) {
                checkoutButton.classList.remove('button--disabled');
                checkoutButton.innerText = 'Checkout Now';
                if (cart.isEmpty()){
                    if(checkoutButton) {
                        checkoutButton.classList.add('button--disabled');
                    }
                }
            }
        });

    }


    handleMethodButtonClick(button: HTMLButtonElement) {

        // Determine method type
        const method = button.getAttribute('data-method-name')?.trim().toLowerCase();
        if (method === '' || method === null || typeof method === 'undefined') {
            this.$log.error('Button does not have a data-method-name attribute', button);
            return;
        }
        /*
            NOTE: if the admin ever changes Zapiets translations
            for the button labels then this functionality will
            break.
            Zapiet translations are edited here:
            https://condor-7.myshopify.com/admin/apps/click-and-collect/settings/design
        */

        // Find the corresponding button
        let foundButton = null;
        const zapietMethodethodButtons = Array.from(
            this.zapietWidget.querySelectorAll('.checkoutMethodsContainer .checkoutMethod')
        );
        for (const zapietMethodethodButton of zapietMethodethodButtons) {
            const buttonTextContent = zapietMethodethodButton.textContent?.trim().toLowerCase();
            if (buttonTextContent && buttonTextContent.match(method)) {

                // Trigger a click and hope it works
                foundButton = zapietMethodethodButton as HTMLElement;
                foundButton.click();
            }
        }
        if (foundButton === null) {
            this.$log.error('Zapiet button not found', method);
            return;
        }

        // Toggle active class
        for (const methodbutton of this.customMethodButton) {
            methodbutton.classList.remove('zapiet-container__method-button--active');
        }
        button.classList.add('zapiet-container__method-button--active');
    }
}

function initializeZapietApp() {
    throw new Error('Function not implemented.');
}

